import {Injectable} from '@angular/core';
import {CardsApiService} from '@atmi/core';
import {setAllOA} from '@atmi/store/oa.repository';
import {setAllF} from '@atmi/store/bene-f.repository';
import {setAllS} from '@atmi/store/bene-s.repository';
import {setAllD} from '@atmi/store/bene-d.repository';
import {setAllMI} from '@atmi/store/bene-mi.repository';
import {CollectionItemApiService} from '@sim/collections/services';
import {firstValueFrom} from 'rxjs';
import {first} from 'rxjs/operators';
import {CollectionApiService} from '../features/collections/services/collection-api.service';

export type CardType = 'oa' | 'f' | 's' | 'd' | 'mi';

export interface ICompressedCard {
  description: string;
  code: string;
  notes: string;
  measures: string;
  id: string;
  material: string;
  sgt: string; // name
  sgtt: string;// titolo
  number: string;
  isParent: boolean;
  price: number;
  previewUrl: string;
  operaMadre: string;
  cardType: CardType; // Type of card
}

// Backward compatibility alias
export type ICompressedOA = ICompressedCard;

export const objRenameProps = (obj: any, propRenameMap: Record<string, string>): any => {
  const newObj: any = {};
  Object.entries(obj).forEach(([key, value]) => {
    newObj[propRenameMap[key] || key] = value;
  });
  return newObj;
};

export const joinObj = (obj: any, skipKey = false): string => {
  if (!obj || typeof obj !== 'object') {
    return '';
  }
  return Object.entries(obj)
    .filter((o) => o[1] && !Array.isArray(o[1]))
    .map((o) => skipKey ? `${o[1]}` : `${o[0]}:${o[1]}`).join(' ');
};

@Injectable({
  providedIn: 'root'
})
export class SearchIndexingService {

  allCompressedOA: ICompressedCard[] = [];

  constructor
  (
    private readonly cardApi: CardsApiService,
    private readonly collectionApi: CollectionApiService,
    private readonly collectionItemsApi: CollectionItemApiService
  ) {
  }

  /*use this service just to load collection into stores
  * do the filtering directly in stores
  * or here set the filter value for the store and in nav-fab-menu
  * just read using proper selector*/

  async init(): Promise<boolean> {

    try {
      // Load collections
      const allCollections = await firstValueFrom(this.collectionApi.getAll().pipe(first()));
      
      // Load all card types
      const allOAres = await firstValueFrom(this.cardApi.getAll().pipe(first()));
      setAllOA(allOAres.data);
      
      // Load BeneF (Fotografie)
      try {
        const allFres = await firstValueFrom(this.cardApi.indexF({}).pipe(first()));
        setAllF(allFres.data);
      } catch (err) {
        console.warn('Failed to load BeneF cards', err);
      }
      
      // Load BeneS (Stampe)
      try {
        const allSres = await firstValueFrom(this.cardApi.indexS({}).pipe(first()));
        setAllS(allSres.data);
      } catch (err) {
        console.warn('Failed to load BeneS cards', err);
      }
      
      // Load BeneD (Disegni)
      try {
        const allDres = await firstValueFrom(this.cardApi.indexD({}).pipe(first()));
        setAllD(allDres.data);
      } catch (err) {
        console.warn('Failed to load BeneD cards', err);
      }
      
      // Load BeneMI (Matrici incise)
      try {
        const allMIres = await firstValueFrom(this.cardApi.indexMI({}).pipe(first()));
        setAllMI(allMIres.data);
      } catch (err) {
        console.warn('Failed to load BeneMI cards', err);
      }
      
      // Load collection items
      // await this.collectionItemsApi.getAll(); // Disabled because it's too slow and not needed globally
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }

  }


  searchAllOA(_searchToken: string, searchData: ICompressedCard[] = this.allCompressedOA): ICompressedCard[] {
    const searchToken = _searchToken.toLowerCase();
    return searchData.filter(card => card.code.includes(searchToken) ||
      card.description.includes(searchToken) || card.sgt.includes(searchToken) || card.notes.includes(searchToken));
  }

  searchInPrice = (_priceMin: number | null,
                   _priceMax: number | null,
                   searchData: ICompressedCard[] = this.allCompressedOA): ICompressedCard[] => {
    if (!_priceMin && !_priceMax) {
      return searchData;
    }
    const priceMin = _priceMin ?? 0;
    const priceMax = _priceMax ?? Number.MAX_SAFE_INTEGER;
    if (priceMin > priceMax) {
      return [];
    }
    return searchData.filter(card => card.price >= priceMin && card.price <= priceMax);
  };
}
